import { makeStyles } from '@material-ui/core/styles';

import { ALMA_ACCENT, MESSAGE_MAX_WIDTH } from './constants';

export const useStyles = makeStyles(theme => ({
  root: {
    bottom: 0,
    position: 'fixed',
    right: 'var(--bui-space-5)',
    zIndex: 1300,
  },
  launcherTab: {
    alignItems: 'center',
    background:
      theme.palette.type === 'dark'
        ? 'var(--palette-neutral-65)'
        : 'var(--palette-neutral-10)',
    border: `1px solid var(--bui-border-1)`,
    borderBottom: 'none',
    borderRadius: 'var(--bui-space-1_5) var(--bui-space-1_5) 0 0',
    cursor: 'pointer',
    display: 'flex',
    fontFamily: 'var(--bui-font-monospace)',
    fontSize: '1.2rem',
    fontWeight: 800,
    gap: '0.1em',
    lineHeight: 1,
    padding: 'var(--bui-space-1) var(--bui-space-4)',
    transition: 'transform 120ms ease',
    '&:hover': {
      transform: 'translateY(-1px)',
    },
  },
  letterLavenderDark: {
    color:
      theme.palette.type === 'dark'
        ? 'var(--palette-lavender-35)'
        : 'var(--palette-lavender-45)',
  },
  letterLavenderLight: {
    color:
      theme.palette.type === 'dark'
        ? 'var(--palette-lavender-25)'
        : 'var(--palette-lavender-35)',
  },
  letterBalticDark: {
    color:
      theme.palette.type === 'dark'
        ? 'var(--palette-baltic-30)'
        : 'var(--palette-baltic-45)',
  },
  letterBalticLight: {
    color:
      theme.palette.type === 'dark'
        ? 'var(--palette-baltic-20)'
        : 'var(--palette-baltic-30)',
  },
  almaLogoSmall: {
    width: 100,
    height: 36,
    borderRadius: 6,
    background: 'transparent',
    marginRight: theme.spacing(1),
    display: 'inline-block',
  },
  panel: {
    position: 'fixed',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: theme.shadows[12],
    overscrollBehavior: 'contain',
    transition:
      'width 225ms cubic-bezier(0.4, 0, 0.2, 1), height 225ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
  panelDragging: {
    transition: 'none',
  },
  panelNormal: {
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  panelFullscreen: {
    position: 'fixed',
    inset: theme.spacing(4),
    borderRadius: 16,
    background: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: theme.shadows[12],
  },
  panelBody: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5),
    background:
      theme.palette.type === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
  },
  headerActionIcon: {
    marginRight: theme.spacing(0.5),
  },

  // Drawer
  drawerPaper: {
    position: 'absolute',
    height: '100%',
    background:
      theme.palette.type === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.grey[50],
    borderRight: 'none',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 225ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
  drawerPaperDragging: {
    transition: 'none',
  },
  drawerResizeHandle: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 5,
    height: '100%',
    padding: 0,
    margin: 0,
    cursor: 'col-resize',
    zIndex: 10,
    background: 'transparent',
    border: 'none',
    borderRight: `1px solid ${theme.palette.divider}`,
    borderRadius: 0,
    '&:hover': {
      borderRight: `2px solid ${theme.palette.primary.main}`,
    },
    '&:disabled': {
      cursor: 'default',
      pointerEvents: 'none',
    },
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  drawerList: {
    flex: 1,
    overflowY: 'auto',
    padding: 0,
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: `${theme.palette.text.disabled} !important`,
      borderRadius: 2,
      border: 'none !important',
    },
  },
  drawerItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  drawerItemActive: {
    background: theme.palette.action.selected,
    '&:hover': {
      background: theme.palette.action.selected,
    },
  },
  drawerDeleteBtn: {
    opacity: 0,
    '$drawerItem:hover &': {
      opacity: 1,
    },
    color: theme.palette.error.main,
  },

  floatingBtn: {
    position: 'absolute',
    top: theme.spacing(0.5),
    zIndex: 5,
    borderRadius: '50%',
    background:
      theme.palette.type === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.background.paper,
    boxShadow: theme.shadows[2],
  },
  hamburgerBtn: {
    left: theme.spacing(0.5),
  },

  // Main chat
  mainColumn: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    position: 'relative',
    transition: 'margin-left 225ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(1.5),
    background: theme.palette.background.default,
    '&::-webkit-scrollbar': { width: '6px !important' },
    '&::-webkit-scrollbar-track': {
      background: `${theme.palette.background.default} !important`,
    },
    '&::-webkit-scrollbar-thumb': {
      background: `${theme.palette.text.disabled} !important`,
      borderRadius: '3px !important',
      border: 'none !important',
    },
  },
  message: {
    padding: theme.spacing(1),
    borderRadius: 10,
    marginBottom: theme.spacing(1),
    maxWidth: MESSAGE_MAX_WIDTH,
    width: 'fit-content',
    boxShadow: theme.shadows[1],
    wordBreak: 'break-word',
  },
  userMsg: {
    marginLeft: 'auto',
    background:
      theme.palette.type === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.common.white,
    border: `1px solid ${theme.palette.primary.main}`,
  },
  userMessageText: { whiteSpace: 'pre-line' },
  botMsgWrap: { display: 'flex', flexDirection: 'column', width: '100%' },
  botMsg: {
    position: 'relative',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(3),
    background:
      theme.palette.type === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.common.white,
    border: `1px solid ${ALMA_ACCENT}`,
  },
  referencesSection: {
    padding: theme.spacing(0.5, 1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  referencesTitle: { fontWeight: 600, marginBottom: theme.spacing(0.25) },
  referencesList: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  referenceItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    lineHeight: 1.4,
  },
  referenceLink: {
    fontSize: '0.72rem',
    maxWidth: '70%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  referenceScore: {
    fontSize: '0.64rem',
    opacity: 0.7,
    cursor: 'default',
    whiteSpace: 'nowrap',
  },
  externalLinkIcon: {
    fontSize: '0.75rem',
    verticalAlign: 'text-bottom',
    marginRight: 2,
  },
  feedbackBar: {
    position: 'absolute',
    bottom: 6,
    right: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    opacity: 0.6,
    '&:hover': { opacity: 1 },
  },
  feedbackText: {
    fontSize: '0.6rem',
    marginRight: 2,
    userSelect: 'none',
    opacity: 0.5,
  },
  feedbackBtn: { padding: 2 },
  feedbackBtnSelectedUp: { color: theme.palette.success.main },
  feedbackBtnSelectedDown: { color: theme.palette.error.main },
  feedbackIcon: { fontSize: '0.85rem' },
  chatErrorMessage: { margin: theme.spacing(1) },
  typingIndicator: { fontStyle: 'italic', opacity: 0.7 },
  markdownScroll: { overflowX: 'auto', maxWidth: '100%' },
  markdownContent: {
    wordBreak: 'normal',
    overflowWrap: 'anywhere',
    '& p': { margin: '0.25em 0' },
    '& ul, & ol': { margin: '0.3em 0', paddingLeft: '1.4em' },
    '& code': {
      fontSize: '0.82em',
      padding: '0.1em 0.35em',
      borderRadius: 4,
      background:
        theme.palette.type === 'dark'
          ? theme.palette.grey[900]
          : theme.palette.grey[100],
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
    '& pre': {
      whiteSpace: 'pre',
      overflowX: 'auto',
      display: 'block',
      maxWidth: '100%',
      margin: '0.4em 0',
    },
    '& pre code': {
      display: 'block',
      padding: '0.5em',
      borderRadius: 4,
      whiteSpace: 'pre',
      wordBreak: 'normal',
      overflowWrap: 'normal',
    },
    '& h1, & h2, & h3, & h4': {
      margin: '0.5em 0 0.2em',
      lineHeight: 1.25,
    },
    '& a': {
      wordBreak: 'break-all',
    },
    '& blockquote': {
      margin: '0.4em 0',
      paddingLeft: '0.8em',
      borderLeft: `3px solid ${theme.palette.divider}`,
    },
    '& img': { maxWidth: '100%', borderRadius: 4 },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      margin: '0.4em 0',
      display: 'block',
      overflowX: 'auto',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: '0.3em 0.5em',
      textAlign: 'left',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
  },
  cautionNote: {
    fontSize: '0.68rem',
    lineHeight: 1.1,
    opacity: 0.8,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.default,
    userSelect: 'none',
    textAlign: 'center',
    padding: theme.spacing(0.5, 1),
  },
  inputBar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    background: theme.palette.background.paper,
  },
  pageContextSwitchWrap: {
    padding: theme.spacing(0, 1.5, 0.5),
  },
  pageContextLabel: {
    marginLeft: 0,
  },
  resizeHandle: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 18,
    height: 18,
    padding: 0,
    margin: 0,
    cursor: 'nwse-resize',
    zIndex: 10,
    background: 'transparent',
    border: 'none',
    borderRadius: 0,
    '&:disabled': {
      cursor: 'default',
      pointerEvents: 'none',
    },
  },
}));
